import { atom, atomFamily, selectorFamily } from 'recoil';
import {
    Color,
    DICOMApprovalStatus,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import { currentStructureSetState } from './structureSetAtoms';

export const defaultGlobalROIContourThickness = 1;

export const openedROIPropertiesOverlayState = atom<boolean>({
    key: 'openedROIPropertiesOverlayState',
    default: false,
});

export const selectedROIState = atom<number>({
    key: 'selectedROIState',
    default: -1,
});

export const openedStructureDeletionConfirmationDialogState = atom<
    number | null
>({
    key: 'openedStructureDeletionConfirmationDialogState',
    default: null,
});

export const openedStructureSetApprovalConfirmationDialogState = atom<{
    sopUid: string;
    newApprovalStatus: DICOMApprovalStatus;
} | null>({
    key: 'openedStructureSetApprovalConfirmationDialogState',
    default: null,
});

export const globalROIContourThicknessState = atom<number>({
    key: 'globalROIContourThicknessState',
    default: defaultGlobalROIContourThickness,
});

export const roisVisibilityState = selectorFamily({
    key: 'roisVisibilityState',
    get:
        (roiNumbers: number[]) =>
        ({ get }) => {
            const visibilityMap = new Map<number, boolean>();
            const selectedROI = get(selectedROIState);
            const currentStructureSet = get(currentStructureSetState);
            roiNumbers.forEach((roiNumber) => {
                const roi = get(
                    roiState({
                        roiNumber,
                        sopUid: currentStructureSet?.dicomUid ?? 'default',
                    })
                );
                const isVisible = roi?.visible ?? false;
                const isSelected = selectedROI === roiNumber;
                visibilityMap.set(roiNumber, isVisible || isSelected);
            });

            return visibilityMap;
        },
});

export const roiColorsState = selectorFamily({
    key: 'roiColorsState',
    get:
        (roiNumbers: number[]) =>
        ({ get }) => {
            const colorsMap = new Map<number, string>();
            const currentStructureSet = get(currentStructureSetState);
            roiNumbers.forEach((roiNumber) => {
                const roi = get(
                    roiState({
                        roiNumber,
                        sopUid: currentStructureSet?.dicomUid ?? 'default',
                    })
                );
                if (!roi) return;
                const { R, G, B } = roi.color;
                const rgbColor = `rgb(${R}, ${G}, ${B})`;
                colorsMap.set(roiNumber, rgbColor);
            });

            return colorsMap;
        },
});

export type ROIState = {
    name: string;
    color: Color;
    visible: boolean;
    approvalStatus: DICOMApprovalStatus;
    contourThickness: number;
    interpretedType: RoiInterpretedType;
    relativeElectronDensity: string | undefined;
    densityOverride: number | undefined;
    identificationCodeDisplay: string;
    roiDescription: string;
    roiNumber: number;
    roiGenerationAlgorithm?: string;
};

export const roiState = atomFamily<
    ROIState | undefined,
    { roiNumber: number; sopUid: string }
>({
    key: 'roiState',
    default: undefined,
});
